<template>
    <div class="w-full px-12 mt-48 xs:w-1/2 sm:w-1/3">
        <!--    CARD START-->
        <div>
            <div class="flex items-center">
                <div class="flex items-center justify-center flex-shrink-0 w-48 h-48 bg-yellow-200 rounded-full">
                    <div class="flex-shrink-0 block w-24 h-24 fill-current text-primary">
                        <IconContactSVG class="w-24 h-24" />
                    </div>
                </div>

                <span class="ml-12 font-normal title-lg text-primary">{{ $t('info.contact_details') }}</span>
            </div>
            <div v-for="contact in filteredContacts" :key="contact.communication_device_id">
                <span
                    class="mt-16 self-label"
                    :class="!contact.value && contact.name != 'Phone' && customerInfo.customertype == 'P' ? 'text-alert' : ''"
                    >{{ $t(contactTranslationsMap[contact.name] || contact.name) }}</span
                >
                <span class="text-base font-semibold sm:text-md">{{ contact.value ?? '-' }}</span>
            </div>

            <span class="mt-16 self-label">{{ $t('info.language') }}</span>
            <span class="text-base font-semibold sm:text-md">{{
                $te('info.langs.' + customerInfo?.preferred_language)
                    ? $t('info.langs.' + customerInfo?.preferred_language)
                    : customerInfo?.preferred_language || '-'
            }}</span>

            <span v-if="customerInfo.customertype === 'O' && customerInfo.website" class="mt-16 self-label">{{
                $t('info.website_address')
            }}</span>
            <span class="text-base font-semibold sm:text-md self-text-link"
                ><a :href="formattedWebsite">{{ customerInfo.website }}</a></span
            >

            <button
                @click="
                    updateCommDevices();
                    openModal('contactsPrivate');
                "
                v-if="customerInfo.customertype === 'P'"
                class="mt-16 self-btn-plain"
            >
                <span class="w-16 ml-0 mr-4 self-btn-plain__icon">
                    <IconPencilSVG />
                </span>
                <span class="self-btn-plain__text">{{ $t('info.modify') }}</span>
            </button>
        </div>
        <!--    CARD END-->

        <!--  PRIVATE CUSTOMER MODAL START  -->
        <div class="micromodal" id="contactsPrivate" aria-hidden="true">
            <div class="px-10 micromodal__overlay" tabindex="-1">
                <div class="micromodal__container" role="dialog" aria-modal="true" aria-labelledby="modal-title">
                    <header class="micromodal__header">
                        <h2 class="pb-24 pr-48 title-2xl text-primary">{{ $t('info.edit_contact_details') }}</h2>

                        <button class="micromodal__close" aria-label="Close modal" data-micromodal-close :disabled="loading">
                            <span class="flex-shrink-0 block w-20 h-20 text-black pointer-events-none fill-current">
                                <IconXSVG />
                            </span>
                        </button>
                    </header>
                    <div class="micromodal__content">
                        <div class="flex self-form-wrap">
                            <div
                                v-if="customerInfo.customertype === 'P'"
                                :class="['w-full self-form-wrap-item', { 'e5-error': !isEmailFormatCorrect }]"
                            >
                                <label
                                    class="e5-form-label"
                                    :class="(!email?.length || deleteEmail) && customerInfo.customertype == 'P' ? 'text-alert' : ''"
                                    for="epost"
                                    >{{ $t('signup.email') }}<span class="e5-form-field-required">*</span></label
                                >
                                <div class="flex items-center">
                                    <input
                                        type="email"
                                        id="epost"
                                        placeholder=""
                                        name="epost"
                                        v-model="email"
                                        class="e5-input"
                                        :disabled="loading"
                                    />
                                </div>
                            </div>

                            <div v-if="customerInfo.customertype === 'P'" class="w-full self-form-wrap-item">
                                <label
                                    class="e5-form-label"
                                    :class="(!mobile?.length || deleteMobilePhone) && customerInfo.customertype == 'P' ? 'text-alert' : ''"
                                    for="mobiil"
                                    >{{ $t('info.mobile_phone') }}<span class="e5-form-field-required">*</span></label
                                >

                                <div class="flex items-center">
                                    <input
                                        type="tel"
                                        id="mobiil"
                                        placeholder=""
                                        name="mobiil"
                                        v-model="mobile"
                                        @input="sanitizePhoneInput('mobile')"
                                        class="e5-input"
                                        :disabled="loading"
                                    />
                                </div>
                            </div>

                            <div v-if="customerInfo.customertype === 'P'" class="w-full self-form-wrap-item">
                                <label
                                    class="e5-form-label"
                                    :class="deletePhone && customerInfo.customertype == 'P' ? 'text-alert' : ''"
                                    for="telefon"
                                    >{{ $t('info.phone') }}</label
                                >
                                <div class="flex items-center">
                                    <input
                                        type="tel"
                                        id="telefon"
                                        placeholder=""
                                        name="telefon"
                                        v-model="phone"
                                        @input="sanitizePhoneInput('phone')"
                                        class="e5-input"
                                        :disabled="loading"
                                    />
                                </div>
                            </div>
                            <div v-if="customerInfo.customertype === 'P'" class="w-full self-form-wrap-item">
                                <label class="e5-form-label" for="language">{{ $t('info.language') }}</label>

                                <div class="flex flex-col gap-2">
                                    <div class="block" v-for="lang in languages" :key="lang.value">
                                        <input
                                            type="radio"
                                            :id="lang.id"
                                            name="language"
                                            :value="lang.value"
                                            class="e5-radio"
                                            v-model="selectedLanguage"
                                            :disabled="loading"
                                        />
                                        <label :for="lang.id">{{ $te(lang.labelKey) ? $t(lang.labelKey) : lang.value }}</label>
                                    </div>
                                </div>
                            </div>
                            <div v-if="deleteMobilePhone || deletePhone || deleteEmail" class="w-full self-form-wrap-item">
                                <AlertSummary :secondary-text="$t('info.delete_contact_details')" />
                            </div>

                            <div class="flex items-center justify-between w-full px-8 pt-16">
                                <div>
                                    <button
                                        @click="submitChanges"
                                        class="self-btn-primary"
                                        :disabled="loading || deleteMobilePhone || deleteEmail || deletePhone || !isEmailFormatCorrect"
                                    >
                                        {{ !loading ? $t('info.save') : '' }}
                                        <span v-if="loading" class="grow-shrink-dot bg-yellow-300" role="status" aria-hidden="true"></span>
                                        <span v-else class="text-yellow-300 self-btn-primary__icon">
                                            <IconArrowAltSVG />
                                        </span>
                                    </button>
                                </div>

                                <div>
                                    <button data-micromodal-close class="self-btn-plain" :disabled="loading">
                                        <span class="w-16 ml-0 mr-4 self-btn-plain__icon">
                                            <IconXSVG />
                                        </span>
                                        <span class="self-btn-plain__text">{{ $t('info.cancel') }}</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!--  PRIVATE CUSTOMER MODAL END  -->
    </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import IconPencilSVG from '@/assets/static/icons-src/icon-pencil.svg';
import IconXSVG from '@/assets/static/icons-src/icon-x.svg';
import IconArrowAltSVG from '@/assets/static/icons-src/icon-arrow-alt.svg';
import IconContactSVG from '@/assets/static/icons-src/icon-contact.svg';
import AlertSummary from '@/components/AlertSummary.vue';
import { ICustomer } from '@/domain/ICustomer';
import { IContacts } from '@/domain/IContacts';
import MicroModal from 'micromodal';
import { ISaveCommunicationDeviceRequest } from '@/domain/ISaveInfo';
import store from '@/store';
import UtilsMixin from '@/scripts/utils';
import validator from 'validator';
@Component({
    components: {
        AlertSummary,
        IconPencilSVG,
        IconXSVG,
        IconArrowAltSVG,
        IconContactSVG
    },
    mixins: [UtilsMixin]
})
export default class ContactInformation extends Vue {
    contactTranslationsMap = {
        'Business Mobile Phone': 'info.business_mobile_phone',
        'Business Phone': 'info.business_phone',
        'Rpid mail': 'info.rpid_email',
        'Business E-mail': 'info.business_email',
        'E-mail': 'signup.email',
        Phone: 'info.phone',
        'Mobile Phone': 'info.mobile_phone'
    };

    loading = false;

    languages = [
        { id: 'et', value: 'Estonian', labelKey: 'info.langs.Estonian' },
        { id: 'en', value: 'English', labelKey: 'info.langs.English' },
        { id: 'ru', value: 'Russian', labelKey: 'info.langs.Russian' },
        { id: 'fi', value: 'Finnish', labelKey: 'info.langs.Finnish' }
    ];

    get customerInfo(): ICustomer {
        return this.$store.getters.getCustomerInfo;
    }

    get customerInfoContacts(): IContacts[] {
        return this.customerInfo.contacts ?? [];
    }

    get filteredContacts(): Partial<IContacts>[] {
        let contacts: Partial<IContacts>[] = [];

        if (this.customerInfo.customertype === 'O') {
            // Filter and sort contacts for organization type 'O'
            contacts = this.customerInfoContacts.filter((contact) => {
                return (
                    ((contact.name === 'Business Mobile Phone' || contact.name === 'Business Phone') &&
                        (contact.reminding_allowed || contact.feedback_allowed)) ||
                    ((contact.name === 'Rpid mail' || contact.name === 'Business E-mail') &&
                        (contact.reminding_allowed || contact.mailing_allowed || contact.feedback_allowed || contact.billing_allowed))
                );
            });
        } else if (this.customerInfo.customertype === 'P') {
            // Filter and sort contacts for private type 'P'
            const email = this.customerInfoContacts.find((contact) => contact.name === 'E-mail');
            contacts.push(email ?? { name: 'E-mail' });
            const phone = this.customerInfoContacts.find((contact) => contact.name === 'Phone');
            contacts.push(phone ?? { name: 'Phone' });
            const mobile = this.customerInfoContacts.find((contact) => contact.name === 'Mobile Phone');
            contacts.push(mobile ?? { name: 'Mobile Phone' });
        }

        // Sort contacts alphabetically by contact.name
        contacts.sort((a, b) => {
            const nameA = a.name.toUpperCase(); // ignore case
            const nameB = b.name.toUpperCase(); // ignore case
            if (nameA < nameB) {
                return -1;
            }
            if (nameA > nameB) {
                return 1;
            }
            return 0;
        });
        return contacts;
    }

    get formattedWebsite() {
        if (!/^https?:\/\//i.test(this.customerInfo.website)) {
            return 'http://' + this.customerInfo.website;
        } else return this.customerInfo.website;
    }

    get deleteEmail(): boolean {
        return (
            !this.email?.length &&
            this.customerInfoContacts.find((contact) => contact.name === 'E-mail')?.value?.length &&
            this.customerInfo?.customertype === 'P'
        );
    }

    get deleteMobilePhone(): boolean {
        return (
            !this.mobile?.length &&
            this.customerInfoContacts.find((contact) => contact.name === 'Mobile Phone')?.value?.length &&
            this.customerInfo?.customertype === 'P'
        );
    }

    get deletePhone(): boolean {
        return (
            !this.phone?.length &&
            this.customerInfoContacts.find((contact) => contact.name === 'Phone')?.value?.length &&
            this.customerInfo?.customertype === 'P'
        );
    }

    get isEmailFormatCorrect(): boolean {
        if (!this.email?.length) return true; // Empty email is valid (not required to be filled)
        return validator.isEmail(this.email.trim());
    }

    // Private customer modal fields for email, phone and mobilephone

    email =
        this.customerInfoContacts.find((contact) => contact.name === 'E-mail') !== undefined
            ? this.customerInfoContacts.find((contact) => contact.name === 'E-mail').value
            : '';

    phone =
        this.customerInfoContacts.find((contact) => contact.name === 'Phone') !== undefined
            ? this.customerInfoContacts.find((contact) => contact.name === 'Phone').value
            : '';

    mobile =
        this.customerInfoContacts.find((contact) => contact.name === 'Mobile Phone') !== undefined
            ? this.customerInfoContacts.find((contact) => contact.name === 'Mobile Phone').value
            : '';

    selectedLanguage = this.customerInfo?.preferred_language;

    /**
     * Update communication device field values from customerinfo if private customer modal is opened.
     */
    updateCommDevices() {
        this.email =
            this.customerInfoContacts.find((contact) => contact.name === 'E-mail') !== undefined
                ? this.customerInfoContacts.find((contact) => contact.name === 'E-mail').value
                : '';
        this.phone =
            this.customerInfoContacts.find((contact) => contact.name === 'Phone') !== undefined
                ? this.customerInfoContacts.find((contact) => contact.name === 'Phone').value
                : '';
        this.mobile =
            this.customerInfoContacts.find((contact) => contact.name === 'Mobile Phone') !== undefined
                ? this.customerInfoContacts.find((contact) => contact.name === 'Mobile Phone').value
                : '';
        this.selectedLanguage = this.customerInfo?.preferred_language;
    }

    /**
     * Confirm changes/deletions made in the contacts and send modification/deletion requests.
     */
    async submitChanges() {
        this.loading = true;

        const results = {
            email: { success: true, attempted: false },
            phone: { success: true, attempted: false },
            mobile: { success: true, attempted: false },
            language: { success: true, attempted: false }
        };

        // Email changes
        const initialEmail =
            this.customerInfoContacts.find((contact) => contact.name === 'E-mail') !== undefined
                ? this.customerInfoContacts.find((contact) => contact.name === 'E-mail')
                : undefined;
        if (initialEmail) {
            // PUT and DELETE requests
            if (initialEmail.value !== this.email.trim()) {
                results.email.attempted = true;
                // Email has been changed and needs to be updated with a put request
                if (this.email.trim() === '') {
                    // Deleting email is not allowed!
                    // const request: IDeleteCommunicationDeviceRequest = {
                    //     customerId: this.customerInfo.customerid,
                    //     communicationDeviceId: initialEmail.communication_device_id
                    // };
                    // results.email.success = await store.dispatch('deleteCommunicationDevice', request);
                } else {
                    const request: ISaveCommunicationDeviceRequest = {
                        customerId: this.customerInfo.customerid,
                        type: initialEmail.type,
                        value: this.email.trim(),
                        communicationDeviceId: initialEmail.communication_device_id
                    };
                    results.email.success = await store.dispatch('saveCommunicationDevice', request);
                }
            }
        } else if (this.email !== '') {
            // POST request
            results.email.attempted = true;
            const request: ISaveCommunicationDeviceRequest = {
                customerId: this.customerInfo.customerid,
                type: 1,
                value: this.email.trim(),
                communicationDeviceId: ''
            };
            results.email.success = await store.dispatch('saveCommunicationDevice', request);
        }

        // Phone changes
        const initialPhone =
            this.customerInfoContacts.find((contact) => contact.name === 'Phone') !== undefined
                ? this.customerInfoContacts.find((contact) => contact.name === 'Phone')
                : undefined;
        if (initialPhone) {
            // PUT and DELETE requests
            if (initialPhone.value !== this.phone) {
                results.phone.attempted = true;
                if (this.phone === '') {
                    // Deleting phone is not allowed!
                    // const request: IDeleteCommunicationDeviceRequest = {
                    //     customerId: this.customerInfo.customerid,
                    //     communicationDeviceId: initialPhone.communication_device_id
                    // };
                    // results.phone.success = await store.dispatch('deleteCommunicationDevice', request);
                } else {
                    const request: ISaveCommunicationDeviceRequest = {
                        customerId: this.customerInfo.customerid,
                        type: initialPhone.type,
                        value: this.phone,
                        communicationDeviceId: initialPhone.communication_device_id
                    };
                    results.phone.success = await store.dispatch('saveCommunicationDevice', request);
                }
            }
        } else if (this.phone !== '') {
            // POST request
            results.phone.attempted = true;
            const request: ISaveCommunicationDeviceRequest = {
                customerId: this.customerInfo.customerid,
                type: 3,
                value: this.phone,
                communicationDeviceId: ''
            };
            results.phone.success = await store.dispatch('saveCommunicationDevice', request);
        }

        // Mobile phone changes
        const initialMobile =
            this.customerInfoContacts.find((contact) => contact.name === 'Mobile Phone') !== undefined
                ? this.customerInfoContacts.find((contact) => contact.name === 'Mobile Phone')
                : undefined;
        if (initialMobile) {
            // PUT and DELETE requests
            if (initialMobile.value !== this.mobile) {
                results.mobile.attempted = true;
                if (this.mobile === '') {
                    // Deleting mobile phone is not allowed!
                    // const request: IDeleteCommunicationDeviceRequest = {
                    //     customerId: this.customerInfo.customerid,
                    //     communicationDeviceId: initialMobile.communication_device_id
                    // };
                    // results.mobile.success = await store.dispatch('deleteCommunicationDevice', request);
                } else {
                    const request: ISaveCommunicationDeviceRequest = {
                        customerId: this.customerInfo.customerid,
                        type: initialMobile.type,
                        value: this.mobile,
                        communicationDeviceId: initialMobile.communication_device_id
                    };
                    results.mobile.success = await store.dispatch('saveCommunicationDevice', request);
                }
            }
        } else if (this.mobile !== '') {
            // POST request
            results.mobile.attempted = true;
            const request: ISaveCommunicationDeviceRequest = {
                customerId: this.customerInfo.customerid,
                type: 2,
                value: this.mobile,
                communicationDeviceId: ''
            };
            results.mobile.success = await store.dispatch('saveCommunicationDevice', request);
        }

        // Language changes
        if (this.selectedLanguage !== this.customerInfo.preferred_language) {
            results.language.attempted = true;
            const request = {
                customerId: this.customerInfo.customerid,
                language: this.selectedLanguage
            };
            results.language.success = await store.dispatch('saveLanguage', request);
        }

        // Check if any requests failed
        const failedRequests = Object.entries(results)
            .filter(([, result]) => result.attempted && !result.success)
            .map(([key]) => key);

        // Only update customer info if at least one request was attempted
        const anyRequestAttempted = Object.values(results).some((result) => result.attempted);
        MicroModal.close('contactsPrivate');
        if (anyRequestAttempted) {
            await store.dispatch('updateCustomerInfo');
        }

        if (failedRequests.length) {
            this.requestErrorFunction(failedRequests);
        }

        this.loading = false;
    }

    sanitizePhoneInput(field: 'mobile' | 'phone') {
        if (field === 'mobile') {
            this.mobile = this.mobile.replace(/[^0-9+]/g, '');
        } else if (field === 'phone') {
            this.phone = this.phone.replace(/[^0-9+]/g, '');
        }
    }

    private requestErrorFunction(failedRequests: string[] = []) {
        const requestNames = {
            email: this.$t('signup.email'),
            phone: this.$t('info.phone'),
            mobile: this.$t('info.mobile_phone'),
            language: this.$t('info.language')
        };

        const failedRequestNames = failedRequests.map((req) => requestNames[req as keyof typeof requestNames] || req);
        const message =
            failedRequestNames.length === 1
                ? (this.$t('notifications.save_error_single', { field: failedRequestNames[0] }) as string)
                : (this.$t('notifications.save_error_multiple', { fields: failedRequestNames.join(', ') }) as string);

        Vue.notify({
            group: 'api',
            title: this.$t('notifications.server_error_title') as string,
            text: message,
            type: 'warn'
        });
    }
}
</script>

<style scoped></style>
